import { useMemo, useCallback } from 'react';
import { useApiClient, useFetchInfinite } from '@marageti/z4-lib';
import { View } from '@marageti/z4-sdk/lib/tracking';
import { ViewsFilters } from '../types';

const DEFAULT_LIMIT = 100;

export const useViews = (filters: ViewsFilters) => {
  const apiClient = useApiClient();

  const getKey = useCallback(
    (pageIndex: number, previousPageData: View[] | null) => {
      if (previousPageData && previousPageData.length < DEFAULT_LIMIT) return null;
      const offset = previousPageData?.length
        ? previousPageData[previousPageData.length - 1].id
        : undefined;
      return ['getViews', filters.entity, filters.day, filters.device, DEFAULT_LIMIT, offset];
    },
    [filters.entity, filters.day, filters.device],
  );

  const fetcher = useCallback(
    ([, entity, day, device, limit, offset]: (string | number | undefined)[]) => (
      apiClient.trackingClient.getViews({
        params: {
          entity, day, device, limit, offset,
        },
      })
    ),
    [apiClient],
  );

  const {
    data,
    error,
    isLoading,
    isValidating,
    size,
    setSize,
  } = useFetchInfinite<View[]>(getKey, fetcher, {
    revalidateFirstPage: false,
    revalidateOnFocus: false,
  });

  const views = useMemo(() => data?.flat() ?? [], [data]);

  const lastPage = data?.[data.length - 1];
  const hasMore = !lastPage || lastPage.length >= DEFAULT_LIMIT;

  const loadMore = useCallback(() => {
    if (!isValidating) setSize(size + 1);
  }, [isValidating, setSize, size]);

  return {
    views,
    isLoading: isLoading || isValidating,
    error: error ?? null,
    hasMore,
    loadMore,
  };
};

export default useViews;
